import React, { FC, useEffect, useState } from "react";
import CustomModal, { ModalSize } from "./CustomModal";
import { LoadingBlock } from "../LoadingBlock";
import { Nullable } from "../../types/custom/Nullable";
import { getHarmonyPolicyPlanInfo } from "../../apiCalls/Harmony/harmonyApiCalls";
import { EnrollmentPackageId, PolicySubscriptionId } from "../../types/api/primaryKeys/PrimaryKey";
import { HarmonyPolicyPlanSummary } from "../../types/api/harmony/HarmonyPolicyPlanSummary";
import { MdSubdirectoryArrowRight } from 'react-icons/md';

interface HarmonyPolicyVolumeModalState {
    showModal: boolean;
    policySubscriptionId: Nullable<PolicySubscriptionId>;
    enrollmentPackageId: Nullable<EnrollmentPackageId>;
    closeModal: () => void;
    policyName: string;
    policyTotal: number;
}

type Props = HarmonyPolicyVolumeModalState;

const HarmonyPlanType = {
    BasePlan: "B",
    Rider: "R",
    Inherent: "I"
};

export const HarmonyPolicyVolumeModal: FC<Props> =
    ({
        showModal,
        policySubscriptionId,
        enrollmentPackageId,
        closeModal,
        policyName,
        policyTotal
    }) => {
        const [isLoading, setLoading] = useState(false);
        const [showError, setError] = useState(false);
        const [policyPlanList, setPlanList] = useState<HarmonyPolicyPlanSummary[]>([]);

        useEffect(() => {
            const getVolumeInfo = async (policySubscriptionId: PolicySubscriptionId, enrollmentPackageId: EnrollmentPackageId) => {
                try {
                    setLoading(true);
                    const result = await getHarmonyPolicyPlanInfo(enrollmentPackageId, policySubscriptionId);
                    setPlanList(result);
                } catch (err) {
                    setError(true);
                    setPlanList([]);
                } finally {
                    setLoading(false);
                }
            };
            if (policySubscriptionId && enrollmentPackageId) {
                getVolumeInfo(policySubscriptionId, enrollmentPackageId);
            }
        }, [policySubscriptionId, enrollmentPackageId]);

        const handleClose = () => {
            setPlanList([]);
            closeModal();
        };

        const renderPolicyPlan = (harmonyPlans: HarmonyPolicyPlanSummary[]) => {
            const isBasePlan = (harmonyPlanType: string) => harmonyPlanType === HarmonyPlanType.BasePlan;
            return harmonyPlans.map((harmonyPlan, index) =>
                <tr key={index}>
                    <td>{!isBasePlan(harmonyPlan.harmonyPlanType) && <MdSubdirectoryArrowRight />}{`${harmonyPlan.harmonyPlanDescription}`}</td>
                    <td></td>
                    <td>{isBasePlan(harmonyPlan.harmonyPlanType) ? `$${harmonyPlan.employeePremium.toFixed(2)}` : ""}</td>
                </tr>
            );
        };
        return (
            <CustomModal
                isModalOpen={showModal}
                modalTitle="Policy Details"
                modalSize={ModalSize.Small}
                includeCloseIcon={true}
                __onCleanup={() => handleClose()}
            >
                <div>
                    {showError && <h2>Sorry! Please try again later</h2>}
                    {isLoading && <LoadingBlock />}
                    {!isLoading && !showError && policyPlanList &&
                        <div className="table-wrapper">
                            <table className="table table-sm table-hover mb-4">
                                <thead>
                                    <tr>
                                        <th>Plan Description</th>
                                        <th></th>
                                        <th>Cost Per Paycheck</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {policyPlanList.length === 0 &&
                                        <tr>
                                            <td>No active plans</td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    }
                                    {policyPlanList.length > 0 && renderPolicyPlan(policyPlanList)}
                                    <tr className="table-secondary">
                                        <td className="bold border-top border-dark">{"Policy: " + policyName}</td>
                                        <td className="bold border-top border-dark">Total: </td>
                                        <td className="bold border-top border-dark">{`$${policyTotal.toFixed(2)}`}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    }
                </div>
            </CustomModal>
        );
    };